import React, { useState, useContext, useRef, useEffect } from 'react';
import { activityContext, AccessInfo } from '../../pages/Dashboard';
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { useNavigate } from "react-router-dom";
import { commonGetService, handleFilterApi } from "../../utils/properties";
import { showToast } from "../../features/Toaster/toastslice";
import { Image } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import "../../styles/Dashboard/idleEmployeesList.scss";

interface UserInfo {
    image: string;
    email: string;
    department: string;
    designation: string;
    employee_id: number;
    employee_code: string;
    employee_name: string;
    device_id: string;
}

interface IdleHours {
    deviceId: string;
    total_time: number;
    idle_time: string;
    idle_time_percentage: number;
    user_info: UserInfo;
}

const IdleEmployeeList = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [idleHoursList, setIdleHoursList] = useState<IdleHours[]>([]);

    //Details for Roles based UI
    const accessInfo: AccessInfo = useContext(activityContext)?.accessInfo as AccessInfo;
    const isTablet: boolean = useContext(activityContext)?.isTablet as boolean;

    const filterData = useAppSelector((state) => state.filterData);
    const { startDate, endDate, empDeviceId, filterType, organization, department, employeeIds } = filterData;
    const targetRef = useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();
    const Navigate = useNavigate();

    useEffect(() => {
        const options = {
            root: null,
            rootMargin: "0px",
            threshold: 0.25,
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (accessInfo["Highest Idle Minutes"]) {
                        getIdleHoursList();
                    }

                    observer.disconnect();
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, options);

        if (targetRef.current) {
            observer.observe(targetRef.current);
        }

        return () => {
            if (targetRef.current) {
                observer.unobserve(targetRef.current);
            }
        };
    }, [startDate, endDate, empDeviceId, filterType, organization, department, accessInfo]);

    const getIdleHoursList = () => {
        setIsLoading(true);
        commonGetService(
            `/api/1.0/dashboard/users/most/idle?${handleFilterApi(startDate, endDate, filterType, organization, empDeviceId, department, true)}&limit=5`
        ).then((res) => {
            setIsLoading(false);
            if (res.status === 200) {
                // Sort by idle_time_percentage descending
                const sorted = [...(res?.data ?? [])].sort(
                    (a: IdleHours, b: IdleHours) => b.idle_time_percentage - a.idle_time_percentage
                );
                setIdleHoursList(sorted);
            } else {
                dispatch(
                    showToast({
                        message: "Something went wrong in API.",
                        status: "retry",
                        visible: true,
                    })
                );
            }
        });
    };

    const handleNavigation = (
        e: React.MouseEvent<Element, MouseEvent>,
        type: string
    ) => {
        e.stopPropagation();
        Navigate(`/reports`, {
            state: {
                reportType: type,
                employeeIds: employeeIds,
                Alluser: filterData.organization === "All Organizations",
                startDate: startDate,
                endDate: endDate,
            },
        });
    };

    return (
        <div className='idle-emp-cont d-flex flex-column' ref={targetRef}>
            <div className="d-flex justify-content-between common-card-title title-ctr">
                <h5>Top idle (by %)</h5>
                <div className="report-link" onClick={(e) => handleNavigation(e, "activity_summary_report")}>
                    View full report
                </div>
            </div>
            <div className='table-cont'>
                <table>
                    <thead>
                        <tr>
                            <th>Emp ID</th>
                            <th>Employee</th>
                            {!isTablet && <th>Email</th>}
                            <th>Designation</th>
                            <th>Department</th>
                            <th>Percentage</th>
                            <th>Hours</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            Array.from({ length: 5 })?.map((_, i) => (
                                <tr key={i}>
                                    <td><Skeleton className='w-75' count={1} /></td>
                                    <td>
                                        <div className='d-flex emp-col align-items-center w-100'>
                                            <Skeleton count={1} circle={true} className="skel-chart" />
                                            <p className='w-75'><Skeleton className='w-100' count={1} /></p>
                                        </div>
                                    </td>
                                    {!isTablet && <td><Skeleton className='w-75' count={1} /></td>}
                                    <td><Skeleton className='w-75' count={1} /></td>
                                    <td><Skeleton className='w-75' count={1} /></td>
                                    <td><Skeleton className='w-75' count={1} /></td>
                                    <td><Skeleton className='w-75' count={1} /></td>
                                </tr>
                            ))
                        ) : idleHoursList.length ? (
                            idleHoursList?.map((res, i) => (
                                <tr key={i}>
                                    <td>{res?.user_info?.employee_code}</td>
                                    <td>
                                        <div className='d-flex emp-col align-items-center'>
                                            {res?.user_info?.image ? (
                                                <Image
                                                    src={res?.user_info?.image}
                                                    alt={res?.user_info?.employee_name}
                                                />
                                            ) : (
                                                <div className="img-avatar d-flex justify-content-center align-items-center">
                                                    <p>{res?.user_info?.employee_name?.charAt(0)}</p>
                                                </div>
                                            )}
                                            <div className='d-flex flex-column emp-col-name'>
                                                {/* <p>{res?.user_info?.employee_name}</p> */}
                                                <p
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => Navigate('/employeeOverview', { state: { empCode: res?.user_info?.employee_code } })}
                                                >
                                                {res?.user_info?.employee_name}
                                                </p>
                                                {isTablet && <p>{res?.user_info?.email}</p>}
                                            </div>
                                        </div>
                                    </td>
                                    {!isTablet && <td>{res?.user_info?.email}</td>}
                                    <td>{res?.user_info?.designation}</td>
                                    <td>{res?.user_info?.department}</td>
                                    <td className="percentage-col">
                                        {res?.idle_time_percentage?.toFixed(2)}%
                                    </td>
                                    <td>{res?.idle_time}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td
                                    className="no-data d-flex flex-column justify-content-center align-items-center"
                                    rowSpan={5}
                                >
                                    <Image
                                        src="/images/dashboard/user_icon.png"
                                        alt="No Data Found"
                                    />
                                    <p className="m-0">No users to show</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default IdleEmployeeList;